/**
 * OAuth Error Handler Utility
 * Comprehensive error handling for OAuth flows
 */

export interface OAuthError {
  code: string;
  message: string;
  details?: string;
  retry?: boolean;
  action?: string;
}

export const OAuthErrorCodes = {
  // Authentication Errors
  POPUP_BLOCKED: 'popup_blocked',
  USER_CANCELLED: 'user_cancelled',
  INVALID_STATE: 'invalid_state',
  INVALID_CODE: 'invalid_code',
  
  // Network Errors
  NETWORK_ERROR: 'network_error',
  SERVER_ERROR: 'server_error',
  TIMEOUT: 'timeout',
  
  // Authorization Errors
  ACCESS_DENIED: 'access_denied',
  INVALID_SCOPE: 'invalid_scope',
  CONSENT_REQUIRED: 'consent_required',
  
  // Token Errors
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_INVALID: 'token_invalid',
  TOKEN_EXCHANGE_FAILED: 'token_exchange_failed',
  
  // Configuration Errors
  INVALID_CLIENT: 'invalid_client',
  INVALID_REDIRECT_URI: 'invalid_redirect_uri',
  PUBLIC_CLIENT_REQUIRED: 'public_client_required',
  
  // Swiss Compliance Errors
  COMPLIANCE_VIOLATION: 'compliance_violation',
  SESSION_EXPIRED: 'session_expired',
  
  // Generic Errors
  UNKNOWN_ERROR: 'unknown_error'
} as const;

export type OAuthErrorCode = typeof OAuthErrorCodes[keyof typeof OAuthErrorCodes];

/**
 * Map OAuth error responses to user-friendly messages
 */
export function mapOAuthError(error: any): OAuthError {
  // Handle string errors
  if (typeof error === 'string') {
    return mapErrorMessage(error);
  }

  // Handle error objects
  const errorCode = error?.code || error?.error || '';
  const errorMessage = error?.message || error?.error_description || error?.detail || '';

  // Check for specific error patterns
  if (errorMessage.includes('popup') || errorMessage.includes('blocked')) {
    return {
      code: OAuthErrorCodes.POPUP_BLOCKED,
      message: 'Pop-up blocked by browser',
      details: 'Please allow pop-ups for this site to connect your Microsoft 365 account.',
      retry: true,
      action: 'Check your browser settings and try again'
    };
  }

  if (errorMessage.includes('cancelled') || errorCode === 'user_cancelled') {
    return {
      code: OAuthErrorCodes.USER_CANCELLED,
      message: 'Authentication cancelled',
      details: 'You cancelled the authentication process.',
      retry: true
    };
  }

  if (errorMessage.includes('state') || errorCode === 'invalid_state') {
    return {
      code: OAuthErrorCodes.INVALID_STATE,
      message: 'Security validation failed',
      details: 'The authentication request was invalid. Please try again.',
      retry: true
    };
  }

  if (errorCode === 'access_denied') {
    return {
      code: OAuthErrorCodes.ACCESS_DENIED,
      message: 'Access denied',
      details: 'You denied access to your Microsoft 365 account. We need this access to provide meeting insights.',
      retry: true,
      action: 'Grant the requested permissions to continue'
    };
  }

  if (errorCode === 'consent_required') {
    return {
      code: OAuthErrorCodes.CONSENT_REQUIRED,
      message: 'Additional consent required',
      details: 'Your administrator requires additional consent for this application.',
      retry: false,
      action: 'Contact your IT administrator'
    };
  }

  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return {
      code: OAuthErrorCodes.NETWORK_ERROR,
      message: 'Network connection error',
      details: 'Unable to connect to the authentication service. Please check your internet connection.',
      retry: true
    };
  }

  if (errorMessage.includes('timeout')) {
    return {
      code: OAuthErrorCodes.TIMEOUT,
      message: 'Request timed out',
      details: 'The authentication request took too long. Please try again.',
      retry: true
    };
  }

  if (errorCode === '401' || errorMessage.includes('expired')) {
    return {
      code: OAuthErrorCodes.TOKEN_EXPIRED,
      message: 'Session expired',
      details: 'Your session has expired. Please reconnect your account.',
      retry: true
    };
  }

  if (errorMessage.includes('exchange') || errorCode === 'invalid_grant') {
    return {
      code: OAuthErrorCodes.TOKEN_EXCHANGE_FAILED,
      message: 'Authentication failed',
      details: 'Unable to complete the authentication process. Please try again.',
      retry: true
    };
  }

  if (errorMessage.includes('public client') || errorMessage.includes('AADSTS7000218')) {
    return {
      code: OAuthErrorCodes.PUBLIC_CLIENT_REQUIRED,
      message: 'App configuration required',
      details: 'Your Azure AD application needs to be configured as a public client.',
      retry: false,
      action: 'In Azure Portal: App Registrations → Your App → Authentication → Advanced settings → Allow public client flows = Yes'
    };
  }

  if (errorMessage.includes('compliance') || errorMessage.includes('8 hour')) {
    return {
      code: OAuthErrorCodes.COMPLIANCE_VIOLATION,
      message: 'Compliance requirement not met',
      details: 'Swiss banking regulations require re-authentication after 8 hours.',
      retry: true,
      action: 'Reconnect your account to continue'
    };
  }

  // Default error
  return {
    code: OAuthErrorCodes.UNKNOWN_ERROR,
    message: 'Authentication error',
    details: errorMessage || 'An unexpected error occurred during authentication.',
    retry: true
  };
}

/**
 * Map simple error messages
 */
function mapErrorMessage(message: string): OAuthError {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('popup')) {
    return mapOAuthError({ error: 'popup_blocked' });
  }

  if (lowerMessage.includes('cancel')) {
    return mapOAuthError({ error: 'user_cancelled' });
  }

  if (lowerMessage.includes('network')) {
    return mapOAuthError({ error: 'network_error' });
  }

  return {
    code: OAuthErrorCodes.UNKNOWN_ERROR,
    message: 'Authentication error',
    details: message,
    retry: true
  };
}

/**
 * Format error for display
 */
export function formatOAuthError(error: OAuthError): string {
  let formatted = error.message;

  if (error.details) {
    formatted += `\n\n${error.details}`;
  }

  if (error.action) {
    formatted += `\n\n${error.action}`;
  }

  return formatted;
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: OAuthError): boolean {
  return error.retry !== false;
}

/**
 * Get error recovery action
 */
export function getErrorRecoveryAction(error: OAuthError): string | null {
  switch (error.code) {
    case OAuthErrorCodes.POPUP_BLOCKED:
      return 'Enable pop-ups and try again';
    
    case OAuthErrorCodes.TOKEN_EXPIRED:
    case OAuthErrorCodes.SESSION_EXPIRED:
      return 'Reconnect your account';
    
    case OAuthErrorCodes.NETWORK_ERROR:
      return 'Check your connection and retry';
    
    case OAuthErrorCodes.CONSENT_REQUIRED:
      return 'Contact your administrator';
    
    case OAuthErrorCodes.PUBLIC_CLIENT_REQUIRED:
      return 'Configure app as public client in Azure Portal';
    
    default:
      return error.action || null;
  }
}